import { i18n, tx, txc } from '@/modules/i18n';
import * as Debug from '@/modules/debug';
import * as Token from '@/modules/token';
import * as Notifications from '@/modules/notifications';
import * as Error_codes from '@/modules/error_codes';


const DATA_TYPES = Object.freeze
( {
	json: Symbol( 'json' ),
	text: Symbol( 'text' ),
	form: Symbol( 'form' ),
	raw: Symbol( 'raw' )
} );


const API_ERRORS = Object.freeze
( {
	invalid_value: 'Invalid value',
	required: 'Required',
	not_found: 'Not Found',
	unauthorized: 'Unauthorized',
	forbidden: 'Forbidden',
	internal: 'Internal Error',
	bad_request: 'Bad Request',
	repeated: 'Repeated value',
	error: 'Error'
} );


let api_url = 'https://mallorca.bankoi.biz/api/' + i18n.lang.value + '/';


//


function Is_unauthorized_status ( status )
{
	return ( status == 401 ) || ( status == 403 );
}


function Can_refresh_status ( status )
{
	return status == 401;
}


function Is_valid_status ( status )
{
	return ( ( status >= 200 ) && ( status <= 299 ) ) || [ 400 ].includes( status );
}


function Get_basic_error ( type, extra = null )
{
	let ack = false;
	let code = type;
	let message = 'Core service error: ';
	
	switch ( type )
	{
		case Error_codes.Basics.fetch_layer:
			message += 'Communication error';
			break;
		
		case Error_codes.Basics.unauthorized:
			ack = true;
			message += 'Unauthorized';
			break;
		
		case Error_codes.Basics.invalid_data:
			message += 'Bad data';
			break;
		
		case Error_codes.Basics.nack:
			message += 'Not acknowledged';
			break;
		
		case Error_codes.Basics.no_payload:
			ack = true;
			message += 'There is no payload';
			break;
		
		case Error_codes.Basics.invalid_status:
			message += 'Invalid HTTP status';
			break;
		
		default:
			code = Error_codes.Basics.bad_configuration;
			message += 'Bad configuration';
	}
	
	Debug.Service.Errors.Add( 'basic error', code, message + ` (${extra})` );
	
	let error = {
		ack: ack,
		ok: false,
		data: null,
		errors: [ code ],
		messages: []
	};
	
	error.messages.push( { type: 'error', code: code, text: message, extra: extra } );
	
	return error;
}


function Get_field_error_message ( type )
{
	switch ( type )
	{
		case API_ERRORS.required: return txc( "This field is required", 'errors' );
		case API_ERRORS.repeated: return txc( "This field cannot be repeated", 'errors' );
		
		case API_ERRORS.invalid_value:
		default: return txc( "Invalid value", 'errors' );
	}
}


function Check_config ( config )
{
	if ( !( 'endpoint' in config ) || !config.endpoint )
		return false;
	
	if ( !( 'verb' in config ) )
		config['verb'] = 'post';
	
	if ( !( 'data_type' in config ) )
		config['data_type'] = DATA_TYPES.json;
	
	if ( !( 'response_type' in config ) )
		config['response_type'] = DATA_TYPES.json;
	
	if ( !( 'use_authorization' in config ) )
		config['use_authorization'] = true;
	
	return true;
}


function Build_request ( config )
{
	let request =
	{
		method: config.verb,
		mode: 'cors',
		headers: {},
		body: null
	};
	
	switch ( config.data_type )
	{
		case DATA_TYPES.text:
			request.headers['Content-Type'] = 'text/plain';
			break;
		
		case DATA_TYPES.form:
			request.headers['Content-Type'] = 'multipart/form-data';
			break;
		
		case DATA_TYPES.json:
		default:
			request.headers['Content-Type'] = 'application/json';
	}
	
	if ( ( 'data' in config ) && config.data )
	{
		switch ( config.data_type )
		{
			case DATA_TYPES.text:
			case DATA_TYPES.raw:
				request.body = config.data;
				break;
			
			case DATA_TYPES.form:
				request.body = new URLSearchParams( config.data );
				break;
			
			case DATA_TYPES.json:
			default:
				request.body = JSON.stringify( config.data );
		}
	}
	
	if ( config.use_authorization )
		request.headers['Authorization'] = 'Bearer ' + Token.Get();
	
	return request;
}


export async function Send ( config )
{
	if ( !Check_config( config ) )
		return Get_basic_error();
	
	let response;
	
	try
	{
		response = await fetch( api_url + config.endpoint, Build_request( config ) );
	}
	catch ( e )
	{
		return Get_basic_error( Error_codes.Basics.fetch_layer );
	}
	
	if ( Is_unauthorized_status( response.status ) )
		return Get_basic_error( Error_codes.Basics.unauthorized );

	if ( Is_valid_status( response.status ) )
	{
		let result;
		
		switch ( config.response_type )
		{
			case DATA_TYPES.raw:
				result = await response.text();
				
				// un resultado vacío en modo texto se considera válido
				return result;
			
			case DATA_TYPES.text:
			case DATA_TYPES.json:
			default:
				try
				{
					result = await response.json();
				}
				catch ( e )
				{
					result = false;
				}
				
				// console.log( result );
				
				if ( !result )
					return Get_basic_error( Error_codes.Basics.invalid_data );
				
				//
				
				let composed_errors = [];
				let composed_messages = [];
				
				if ( 'error' in result )
				{
					if ( ( result.error?.type == API_ERRORS.bad_request ) && result.error?.details )
					{
						result.error.details.forEach( item =>
						{
							composed_errors.push( Error_codes.Data.bad_data );
							composed_messages.push
							(
								{
									type: 'data',
									code: Error_codes.Data.bad_data,
									extra: item?.property,
									text: Get_field_error_message( item?.type )
								}
							);
						} );
					}
					
					else if (
						( result.error?.type == API_ERRORS.invalid_value )
						||
						( result.error?.type == API_ERRORS.repeated )
						||
						( result.error?.type == API_ERRORS.required )
					)
					{
						composed_errors.push( Error_codes.Data.bad_data );
						composed_messages.push
						(
							{
								type: 'data',
								code: Error_codes.Data.bad_data,
								extra: result.error?.property,
								text: Get_field_error_message( result.error?.type )
							}
						);
					}
					
					else if ( result.error?.type == API_ERRORS.not_found )
					{
						composed_errors.push( Error_codes.Data.bad_data );
						composed_messages.push
						(
							{
								type: 'data',
								code: Error_codes.Data.not_found,
								extra: null,
								text: txc( "The data couldn't be retrieved", 'errors' )
							}
						);
					}
					
					else
						return Get_basic_error( Error_codes.Basics.nack );
				}
				
				//
				
				let composed_result = {
					ack: true,
					ok: result?.ok,
					data: 'data' in result ? result.data : null,
					errors: composed_errors,
					messages: composed_messages
				};
				
				return composed_result;
				
				
				// if ( !result?.ok )
				// 	return Get_basic_error( Error_codes.Basics.nack );
				
				// if ( !( 'payload' in result ) )
				// 	return Get_basic_error( Error_codes.Basics.no_payload );

				// if ( result.payload?.performance?.received && result.payload?.performance?.finalized )
				// {
				// 	Debug.Service.Performance.Add
				// 	(
				// 		config.endpoint,
				// 		( result.payload.performance.finalized - result.payload.performance.received ).toFixed( 5 )
				// 	);
				// }
				
				// return result.payload;
		}
	}
	
	return Get_basic_error( Error_codes.Basics.invalid_status, response.status );
}


// Solicita un dato de tipo JSON.
export async function Request ( endpoint, data = null )
{
	return await Send( {
		endpoint: endpoint,
		data: data
	} );
}


// Solicita un dato de tipo texto plano (p.ej. HTML)
export async function Fetch ( endpoint, data = null )
{
	return await Send( {
		endpoint: endpoint,
		data: data
	} );
}


// Solicita texto plano, usar con cuidado.
export async function Text ( endpoint, data = null )
{
	return await Send( {
		endpoint: endpoint,
		response_type: DATA_TYPES.text,
		data: data
	} );
}


export function Show_result_messages ( result )
{
	if ( result.messages.length > 0 )
	{
		result.messages.forEach( message =>
		{
			const text = message?.text + ( message?.extra ? ` (${message.extra})`: '' );
			
			switch ( message?.type )
			{
				case 'error':
					Notifications.Error( text );
					Debug.Service.Errors.Add( 'result', message?.code, text );
					break;
				
				case 'warning':
					Notifications.Warning( message?.text );
					break;
				
				case 'info':
					Notifications.Info( message?.text );
					break;
			}
		} );
	}
}
